import React, { useState, useRef, useContext } from "react";
import "./profilePhotoModal.css";
import companyBasedRequest from "../../../request/companyBasedRequest";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";

const ProfileUploadModal = ({ staffProfile, isOpen, onClose, onUploaded }) => {
	const { userData, language } = useContext(CombinedContext);

	const fileRef = useRef(null);
	const [preview, setPreview] = useState(null);
	const [file, setFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState("");
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);

	if (!isOpen) return null;

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);
		setTimeout(() => setExiting(true), duration - 250);
		setTimeout(() => { setBanner(null); setExiting(false); }, duration);
	};

	const pickFile = () => fileRef.current?.click();

	const fileToBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	};

	const handleFile = (e) => {
		const f = e.target.files[0];
		if (!f) return;

		if (!f.type.startsWith("image/")) {
			setError("Please upload a valid image.");
			return;
		}

		setError("");
		setFile(f);
		setPreview(URL.createObjectURL(f));
	};

	async function shrinkImage(file, maxWidth = 1080, quality = 0.8) {
		return new Promise((resolve, reject) => {
			const img = new Image();
			const reader = new FileReader();

			reader.onload = (e) => {
				img.src = e.target.result;
			};

			img.onload = () => {
				const canvas = document.createElement("canvas");
				const scale = maxWidth / img.width;
				const newWidth = img.width > maxWidth ? maxWidth : img.width;
				const newHeight = img.height > maxWidth ? img.height * scale : img.height;

				canvas.width = newWidth;
				canvas.height = newHeight;

				const ctx = canvas.getContext("2d");
				ctx.drawImage(img, 0, 0, newWidth, newHeight);

				canvas.toBlob(
					(blob) => {
						resolve(blob);
					},
					"image/jpeg",
					quality // compression quality (0.8)
				);
			};

			img.onerror = reject;
			reader.readAsDataURL(file);
		});
	}

	const handleUpload = async () => {
		if (!file) {
			setError("Please choose an image first.");
			return;
		}

		try {
			setUploading(true);
			setError("");

			// ⬅️ SHRINK IT FIRST
			const shrunkBlob = await shrinkImage(file, 1080, 0.8);

			// convert blob → base64
			const base64 = await new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result.split(",")[1]);
				reader.onerror = reject;
				reader.readAsDataURL(shrunkBlob);
			});

			const userId = staffProfile.staffId || staffProfile.userId

			const result = await companyBasedRequest.uploadProfilePhoto(
				userData.user.shopId,
				userId,
				base64,
				"image/jpeg",
				userData.cognitoTokens.AccessToken
			);

			if (onUploaded) onUploaded(result.photoUrl);
			onClose();
		} catch (err) {
			console.error(err);
			setError("Failed to upload image.");
		} finally {
			setUploading(false);
		}
	};

	return (
		<div className="sf-avatar-overlay" onClick={onClose}>
			<div className="sf-avatar-modal" onClick={(e) => e.stopPropagation()}>
				<h2 className="sf-avatar-title">Update Profile Photo</h2>

				{/* PREVIEW */}
				<div className="sf-avatar-preview-wrapper">
					{preview ? (
						<img src={preview} alt="Preview" className="sf-avatar-preview" />
					) : (
						<div className="sf-avatar-placeholder">{translations[language].profile?.images?.noImageSelected || "No image selected"}</div>
					)}
				</div>

				{/* ERROR */}
				{error && <div className="sf-avatar-error">{error}</div>}

				<input type="file" accept="image/*" ref={fileRef} style={{ display: "none" }} onChange={handleFile} />

				<div className="sf-avatar-actions">
					<button className="sf-avatar-btn ghost" onClick={pickFile}>
						Choose Image
					</button>

					<button className="sf-avatar-btn primary" onClick={handleUpload} disabled={uploading}>
						{uploading ? "Uploading..." : "Save Photo"}
					</button>
				</div>

				<button className="sf-avatar-close" onClick={onClose}>
					✕
				</button>
			</div>
		</div>
	);
};

export default ProfileUploadModal;

